import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import {
  useDeletePolicyMutation,
  useListAllPoliciesQuery,
} from "api/memberPortal/policies/policies.api";
import { AdminPolicyLetter } from "api/memberPortal/policies/policies.types";
import DefaultDataGrid from "components/MUI/DataGrid/DefaultDataGrid";
import DeleteModal from "components/UI/DeleteModal";

import PolicyLetterModal from "./PolicyLetterModal";
import { buildPolicyLettersColumns } from "./PolicyLetters.columns";

const EMPTY_POLICIES: AdminPolicyLetter[] = [];

export default function PolicyLettersTable() {
  const [alert, setAlert] = useState({ message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPolicyLetterModal, setShowPolicyLetterModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currPolicy, setCurrPolicy] = useState<AdminPolicyLetter | null>(null);
  const [policyId, setPolicyId] = useState<number | null>(null);

  const {
    data: policies = EMPTY_POLICIES,
    refetch,
    isLoading,
  } = useListAllPoliciesQuery();
  const [deletePolicy] = useDeletePolicyMutation();

  const deletePolicyLetter = async () => {
    if (!policyId) return;

    try {
      await deletePolicy(policyId).unwrap();
      setAlert({
        message: "Policy Letter Successfully Deleted",
        type: "success",
      });
      setShowDeleteModal(false);
    } catch {
      setAlert({
        message:
          "Cannot delete Policy Letter because it is being used somewhere else",
        type: "error",
      });
    }
  };

  const columns = useMemo(
    () =>
      buildPolicyLettersColumns({
        onEdit: (policy) => {
          setCurrPolicy(policy);
          setIsEditing(true);
          setShowPolicyLetterModal(true);
        },
        onDelete: (id) => {
          setPolicyId(id);
          setShowDeleteModal(true);
        },
      }),
    [],
  );

  return (
    <Box>
      <PolicyLetterModal
        show={showPolicyLetterModal}
        setShow={setShowPolicyLetterModal}
        currentPolicy={currPolicy}
        // TODO: Refactor modal to not need the refetch prop by using RTKQ on the POST and invalidating the tags
        refetch={refetch}
        editingPolicy={isEditing}
        setAlert={setAlert}
        setEditingPolicy={setIsEditing}
      />
      {/* @ts-ignore - this looks like an older modal not setup to handle types yet  */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        type="Policy Letter"
        deleteFunction={deletePolicyLetter}
      />

      {alert.message && (
        <Alert
          severity={alert.type as "success" | "error"}
          onClose={() => setAlert({ message: "", type: "" })}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3, mt: 5 }}
      >
        <Typography variant="h5" color="primary">
          Policy Letters
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setIsEditing(false);
            setCurrPolicy(null);
            setShowPolicyLetterModal(true);
          }}
        >
          + Add Policy Letter
        </Button>
      </Stack>

      <DefaultDataGrid
        columns={columns}
        rows={policies}
        getRowId={(row) => row.policy_id}
        loading={isLoading}
      />
    </Box>
  );
}
